import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEquipmentTypeReviews,
  getEquipmentTypeReviewSummary,
  getReservationReviewStatus,
  submitReservationReview,
  updateReservationReview,
} from '@/api/reviews';
import type {
  EquipmentTypeReviewsRequest,
  SubmitReservationReviewRequest,
  UpdateReservationReviewRequest,
} from '@/types/reviews';
import { reservationKeys } from '@/hooks/useReservations';

export const reviewKeys = {
  all: ['reviews'] as const,
  reservationStatus: (reservationId: string) => [...reviewKeys.all, 'reservation-status', reservationId] as const,
  equipmentTypeReviews: (equipmentTypeId: string, params: EquipmentTypeReviewsRequest) =>
    [...reviewKeys.all, 'equipment-type-reviews', equipmentTypeId, params] as const,
  equipmentTypeSummary: (equipmentTypeId: string) =>
    [...reviewKeys.all, 'equipment-type-summary', equipmentTypeId] as const,
};

export const useReservationReviewStatus = (reservationId: string, enabled = true) => {
  return useQuery({
    queryKey: reviewKeys.reservationStatus(reservationId),
    queryFn: () => getReservationReviewStatus(reservationId),
    enabled: Boolean(reservationId) && enabled,
    staleTime: 60 * 1000,
  });
};

export const useSubmitReservationReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SubmitReservationReviewRequest) => submitReservationReview(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.reservationStatus(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.detail(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
    },
  });
};

export const useUpdateReservationReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reservationId, request }: { reservationId: string; request: UpdateReservationReviewRequest }) =>
      updateReservationReview(reservationId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.reservationStatus(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.detail(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
    },
  });
};

export const useEquipmentTypeReviews = (
  equipmentTypeId: string | undefined,
  params: EquipmentTypeReviewsRequest = {},
) => {
  return useQuery({
    queryKey: reviewKeys.equipmentTypeReviews(equipmentTypeId ?? 'unknown', params),
    queryFn: () => getEquipmentTypeReviews(equipmentTypeId!, params),
    enabled: Boolean(equipmentTypeId),
    staleTime: 2 * 60 * 1000,
  });
};

export const useEquipmentTypeReviewSummary = (equipmentTypeId: string | undefined) => {
  return useQuery({
    queryKey: reviewKeys.equipmentTypeSummary(equipmentTypeId ?? 'unknown'),
    queryFn: () => getEquipmentTypeReviewSummary(equipmentTypeId!),
    enabled: Boolean(equipmentTypeId),
    staleTime: 2 * 60 * 1000,
  });
};
